import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { TokenManager } from '../services/apiClient';
import { ENV } from '../config/env';

// Auth Pages (eager load - small bundle)
import { LoginPage } from './components/auth-pages/LoginPage';
import { SignUpPage } from './components/auth-pages/SignUpPage';
import { CompleteSignUpPage } from './components/auth-pages/CompleteSignUpPage';
import { ResetPasswordPage } from './components/auth-pages/ResetPasswordPage';
import { VerifyEmailPage } from './components/auth-pages/VerifyEmailPage';
import { MagicLinkPage } from './components/auth-pages/MagicLinkPage';
import { AcceptInvitePage } from './components/auth-pages/AcceptInvitePage';

// Dashboard Layout (eager load)
import { DashboardLayout } from './components/DashboardLayout';
import { RequirePlan } from './components/RequirePlan';
import { RequireSuperAdmin } from './components/RequireSuperAdmin';

// Dashboard Pages (lazy load - code splitting!)
const CPIDashboard = lazy(() => import('./components/CPIDashboard').then(m => ({ default: m.CPIDashboard })));
const PolicyBundleExportPage = lazy(() => import('./components/PolicyBundleExportPage').then(m => ({ default: m.PolicyBundleExportPage })));
const PolicyApprovalQueuePage = lazy(() => import('./components/PolicyApprovalQueuePage').then(m => ({ default: m.PolicyApprovalQueuePage })));
const PolicyEffectivenessDashboardPage = lazy(() => import('./components/PolicyEffectivenessDashboardPage').then(m => ({ default: m.PolicyEffectivenessDashboardPage })));
const PolicyAuditorViewPage = lazy(() => import('./components/PolicyAuditorViewPage').then(m => ({ default: m.PolicyAuditorViewPage })));
const PolicyInsuranceFeedPage = lazy(() => import('./components/PolicyInsuranceFeedPage').then(m => ({ default: m.PolicyInsuranceFeedPage })));
const PolicyAdlEditorPage = lazy(() => import('./components/PolicyAdlEditorPage').then(m => ({ default: m.PolicyAdlEditorPage })));
const PolicyDetailPage = lazy(() => import('./components/PolicyDetailPage').then(m => ({ default: m.PolicyDetailPage })));
const TeamManagement = lazy(() => import('./components/TeamManagement').then(m => ({ default: m.TeamManagement })));
const AlertsPage = lazy(() => import('./components/AlertsPage').then(m => ({ default: m.AlertsPage })));
const SystemDetailPage = lazy(() => import('./components/pages/systems/SystemDetailPage').then(m => ({ default: m.SystemDetailPage })));
const AgentOnboardingPage = lazy(() => import('./components/AgentOnboardingPage').then(m => ({ default: m.AgentOnboardingPage })));
const DecisionMining = lazy(() => import('./components/DecisionMining').then(m => ({ default: m.DecisionMining })));
const FeedbackAnalyticsPage = lazy(() => import('./components/FeedbackAnalyticsPage').then(m => ({ default: m.FeedbackAnalyticsPage })));
const SettingsPage = lazy(() => import('./components/SettingsPage').then(m => ({ default: m.SettingsPage })));
const BillingPage = lazy(() => import('./components/BillingPage').then(m => ({ default: m.BillingPage })));
const AuditLogPage = lazy(() => import('./components/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const ProfilePage = lazy(() => import('./components/ProfilePage').then(m => ({ default: m.ProfilePage })));
const VerifyEmailChangePage = lazy(() => import('./components/VerifyEmailChangePage').then(m => ({ default: m.VerifyEmailChangePage })));
// SecuritySettingsPage / LoginActivityPage / PasskeyManagementPage /
// PrivacyConsentPage are no longer routed standalone — ProfilePage
// imports them lazily itself for the tab content. Keeping route-level
// lazy imports here would bundle them twice.

// NEW: Super Admin Panel (lazy load)
const SuperAdminPanel = lazy(() => import('./components/SuperAdminPanel').then(m => ({ default: m.SuperAdminPanel })));

// NEW: Founder Analytics (#50 Sub-Task D)
const FounderAnalyticsPage = lazy(() => import('./components/FounderAnalyticsPage').then(m => ({ default: m.FounderAnalyticsPage })));

// NEW: Scheduled Reports
const NotificationsPage = lazy(() => import('./components/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

// NEW: Phase-1 Workspace Onboarding
const WelcomePage = lazy(() => import('./components/onboarding/WelcomePage').then(m => ({ default: m.WelcomePage })));
const TrustCenterPage = lazy(() => import('./components/onboarding/TrustCenterPage').then(m => ({ default: m.TrustCenterPage })));
const CoveragePage = lazy(() => import('./components/onboarding/CoveragePage').then(m => ({ default: m.CoveragePage })));
const BiasMonitoringSetupPage = lazy(() => import('./components/onboarding/BiasMonitoringSetupPage').then(m => ({ default: m.BiasMonitoringSetupPage })));

// NEW: Compliance suite (Phase 1 + 2 + 3)
// NotaryAttestationsPage + BundleExportPage are imported directly by EvidenceExportsPage
const InsuranceFeedPage = lazy(() => import('./components/InsuranceFeedPage').then(m => ({ default: m.InsuranceFeedPage })));
const WitnessAdminPage = lazy(() => import('./components/WitnessAdminPage').then(m => ({ default: m.WitnessAdminPage })));
const ComplianceMappingPage = lazy(() => import('./components/ComplianceMappingPage').then(m => ({ default: m.ComplianceMappingPage })));
// Council Sprint 3 F-42 (2026-05-21): GDPR Art. 30 ROPA auto-generator
const RopaPage = lazy(() => import('./components/RopaPage').then(m => ({ default: m.RopaPage })));
// Council Sprint Zero F-46 (2026-05-21): Compliance Maturity Registry Admin UI
const ComplianceMaturityAdminPage = lazy(() => import('./components/ComplianceMaturityAdminPage').then(m => ({ default: m.ComplianceMaturityAdminPage })));
const IncidentDetailPage = lazy(() => import('./components/pages/incidents/IncidentDetailPage').then(m => ({ default: m.IncidentDetailPage })));
// Old Provider-Tech-Doc / Deployer / FRIA pages DELETED 2026-06-09 — their content
// is being rebuilt natively in the EU-AI-Act hub (Tabs „Technische Doku" /
// „Deployer & FRIA"). Old routes redirect to the hub.
// Controller-Responsibility catalog — the ⚪ "you must do this" attestation
// surface (renders /api/controller-responsibilities; covers Art. 26(4)/(7)/(11)/(12)
// + every other framework's non-delegable controller duties).
const ControllerResponsibilitiesPage = lazy(() => import('./components/ControllerResponsibilitiesPage').then(m => ({ default: m.ControllerResponsibilitiesPage })));
const FederatedLearningPage = lazy(() => import('./components/FederatedLearningPage').then(m => ({ default: m.FederatedLearningPage })));
const PrivacyBudgetPage = lazy(() => import('./components/PrivacyBudgetPage').then(m => ({ default: m.PrivacyBudgetPage })));
// Council Sprint 4 F-26 (2026-05-21): Regulator Replay standalone UI
const RegulatorReplayPage = lazy(() => import('./components/RegulatorReplayPage').then(m => ({ default: m.RegulatorReplayPage })));
// Council Sprint 4 F-16 (2026-05-21): TEE Attestation Customer-UI (Custom-tier)
const TeeAttestationPage = lazy(() => import('./components/TeeAttestationPage').then(m => ({ default: m.TeeAttestationPage })));

// VVG §61/§62 — Advisory Records (Insurance Documentation)

// NEW IA (2026-05-30) — Blank stubs for NEW sidebar structure
const OverviewPage = lazy(() => import('./components/pages/OverviewPage').then(m => ({ default: m.OverviewPage })));
const SystemsPage = lazy(() => import('./components/pages/SystemsPage').then(m => ({ default: m.SystemsPage })));
const ConnectorsPage = lazy(() => import('./components/pages/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })));
const DecisionLogPage = lazy(() => import('./components/pages/DecisionLogPage').then(m => ({ default: m.DecisionLogPage })));
const ReviewQueuePage = lazy(() => import('./components/pages/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage })));
const AnomaliesPage = lazy(() => import('./components/pages/AnomaliesPage').then(m => ({ default: m.AnomaliesPage })));
const AuditChainPage = lazy(() => import('./components/pages/AuditChainPage').then(m => ({ default: m.AuditChainPage })));
const ProvenancePage = lazy(() => import('./components/pages/ProvenancePage').then(m => ({ default: m.ProvenancePage })));
const EvidenceExportsPage = lazy(() => import('./components/pages/EvidenceExportsPage').then(m => ({ default: m.EvidenceExportsPage })));
const IncidentsPage = lazy(() => import('./components/pages/IncidentsPage').then(m => ({ default: m.IncidentsPage })));
const ErasurePage = lazy(() => import('./components/pages/ErasurePage').then(m => ({ default: m.ErasurePage })));
const PolicyEnginePage = lazy(() => import('./components/pages/PolicyEnginePage').then(m => ({ default: m.PolicyEnginePage })));
const CalibrationConfidencePage = lazy(() => import('./components/pages/CalibrationConfidencePage').then(m => ({ default: m.CalibrationConfidencePage })));
const FairnessBiasPage = lazy(() => import('./components/pages/FairnessBiasPage').then(m => ({ default: m.FairnessBiasPage })));
const ComplianceOverviewPage = lazy(() => import('./components/pages/ComplianceOverviewPage').then(m => ({ default: m.ComplianceOverviewPage })));
const EuAiActPage = lazy(() => import('./components/pages/EuAiActPage').then(m => ({ default: m.EuAiActPage })));
const GdprPage = lazy(() => import('./components/pages/GdprPage').then(m => ({ default: m.GdprPage })));
const AmlGwgPage = lazy(() => import('./components/pages/AmlGwgPage').then(m => ({ default: m.AmlGwgPage })));
const BeratungPage = lazy(() => import('./components/pages/BeratungPage').then(m => ({ default: m.BeratungPage })));
const DoraPage = lazy(() => import('./components/pages/DoraPage').then(m => ({ default: m.DoraPage })));
const SolvencyVagPage = lazy(() => import('./components/pages/SolvencyVagPage').then(m => ({ default: m.SolvencyVagPage })));
const EiopaPage = lazy(() => import('./components/pages/EiopaPage').then(m => ({ default: m.EiopaPage })));
const DataActFidaPage = lazy(() => import('./components/pages/DataActFidaPage').then(m => ({ default: m.DataActFidaPage })));
const AnalyticsPage = lazy(() => import('./components/pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const ReportsPage = lazy(() => import('./components/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));

// Additional Auth Pages

// Error Pages
import { NotFoundPage } from './components/landing-page/NotFoundPage';
import { StubPage } from './components/pages/StubPage';
import { ErrorPage } from './components/landing-page/ErrorPage';
import { ErrorPage as DashboardErrorPage } from './components/dashboard/ErrorPage';

// OAuth Callback
// Backend sets an httpOnly cookie (adjudon_oauth_token) and redirects here.
// We exchange that cookie for a JWT via a backend endpoint so the token
// is validated server-side before being stored — prevents crafted-URL attacks.
function OAuthCallback() {
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    const oauthUrl = ENV.API_BASE_URL
      ? `${ENV.API_BASE_URL}/api/auth/oauth-token`
      : '/api/auth/oauth-token';
    fetch(oauthUrl, {
      method: 'GET',
      credentials: 'include', // send the httpOnly cookie
    })
      .then(r => r.json())
      .then(data => {
        if (data?.data?.token) {
          TokenManager.setAccessToken(data.data.token);
          window.location.href = '/dashboard';
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true));
  }, []);

  if (error) {
    window.location.href = '/login?error=oauth_failed';
    return null;
  }
  return null;
}

// Org + Workspace Picker
import { WorkspaceDashboardPage } from './components/WorkspaceDashboardPage';

// Help & marketing surfaces (workspace-onboarding wizard removed 2026-05-02)
import { PricingPage } from './components/landing-page/PricingPage';
import { DocsPage } from './components/landing-page/DocsPage';

// Agents→Systems rename (2026-06-03): forward the retired /dashboard/agents*
// paths to /dashboard/systems*, preserving query string and :id so legacy
// deep-links (and bookmarks) keep working.
function RedirectAgentsToSystems() {
  const { search } = useLocation();
  return <Navigate to={`/dashboard/systems${search}`} replace />;
}

function RedirectAgentDetailToSystem() {
  const { agentId } = useParams<{ agentId: string }>();
  const { search } = useLocation();
  return <Navigate to={`/dashboard/systems/${agentId ?? ''}${search}`} replace />;
}

// Wrapper for lazy loaded components
function LazyWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Redirect root to dashboard (since marketing site is on a separate domain)
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },

  // OAuth Callback (handles token from backend redirect)
  {
    path: '/auth/callback',
    element: <OAuthCallback />,
    errorElement: <ErrorPage />,
  },

  // Public Auth Routes
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/signup',
    element: <SignUpPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/signup/complete',
    element: <CompleteSignUpPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/auth/magic-link',
    element: <MagicLinkPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvitePage />,
    errorElement: <ErrorPage />,
  },


  // Public Pages
  {
    path: '/pricing',
    element: <PricingPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: '/help',
    element: <DocsPage />,
    errorElement: <ErrorPage />,
  },
  // (Workspace-onboarding wizard removed 2026-05-02 — first-time users go
  //  straight to /dashboard. Per-agent onboarding lives at
  //  /dashboard/agents/new.)

  // Phase-1 onboarding entry point — protected, fresh signups land here
  {
    path: '/welcome',
    element: (
      <ProtectedRoute>
        <LazyWrapper><WelcomePage /></LazyWrapper>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },

  // Trust Center — public-ish (auth required), lists legal docs + sub-processors
  {
    path: '/trust',
    element: (
      <ProtectedRoute>
        <LazyWrapper><TrustCenterPage /></LazyWrapper>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },

  // Coverage — what frameworks ADJUDON helps with (EU AI Act, ISO 42001, DORA, …)
  {
    path: '/coverage',
    element: (
      <ProtectedRoute>
        <LazyWrapper><CoveragePage /></LazyWrapper>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
  },

  // Protected Dashboard Routes
  {
    path: '/dashboard',
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      </ErrorBoundary>
    ),
    errorElement: <DashboardErrorPage />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <OverviewPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'overview',
        element: (
          <LazyWrapper>
            <OverviewPage />
          </LazyWrapper>
        ),
      },
      // Keep-clean allow-list routes (NOT moved under /o)
      {
        path: 'profile',
        element: (
          <LazyWrapper>
            <ProfilePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'profile/verify-email-change',
        element: (
          <LazyWrapper>
            <VerifyEmailChangePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'workspaces',
        element: <WorkspaceDashboardPage />,
      },
      {
        path: 'super-admin',
        element: (
          <LazyWrapper>
            <RequireSuperAdmin featureName="Super Admin">
              <SuperAdminPanel />
            </RequireSuperAdmin>
          </LazyWrapper>
        ),
      },
      {
        path: 'founder-analytics',
        element: (
          <LazyWrapper>
            <RequireSuperAdmin featureName="Founder Analytics">
              <FounderAnalyticsPage />
            </RequireSuperAdmin>
          </LazyWrapper>
        ),
      },
      {
        path: 'super-admin/witnesses',
        element: (
          <LazyWrapper>
            <RequireSuperAdmin featureName="Witness Network — Admin">
              <WitnessAdminPage />
            </RequireSuperAdmin>
          </LazyWrapper>
        ),
      },
      {
        path: 'super-admin/compliance-maturity',
        element: (
          <LazyWrapper>
            <RequireSuperAdmin featureName="Compliance Maturity Registry">
              <ComplianceMaturityAdminPage />
            </RequireSuperAdmin>
          </LazyWrapper>
        ),
      },
      {
        path: 'audit/witnesses',
        element: <Navigate to="/dashboard/super-admin/witnesses" replace />,
      },

      // ── OLD routes moved under /dashboard/o ──────────────────────────────
      {
        path: 'o',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <LazyWrapper>
                <StubPage title="Übersicht (alt)" />
              </LazyWrapper>
            ),
          },
          {
            path: 'cpi',
        element: (
          <LazyWrapper>
            <CPIDashboard />
          </LazyWrapper>
        ),
      },
      {
        path: 'calibration',
        element: (
          <LazyWrapper>
            <StubPage title="Kalibrierung (alt)" />
          </LazyWrapper>
        ),
      },
      {
        path: 'calibration/bias-setup',
        element: (
          <LazyWrapper>
            <BiasMonitoringSetupPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'review-queue',
        element: (
          <LazyWrapper>
            <StubPage title="Review-Queue (alt)" />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies',
        element: (
          <LazyWrapper>
            <StubPage title="Policy Engine (alt)" />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/export',
        element: (
          <LazyWrapper>
            <PolicyBundleExportPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/approvals',
        element: (
          <LazyWrapper>
            <PolicyApprovalQueuePage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/effectiveness',
        element: (
          <LazyWrapper>
            <PolicyEffectivenessDashboardPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/insurance-feed',
        element: (
          <LazyWrapper>
            <PolicyInsuranceFeedPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'audit/policy-engine',
        element: (
          <LazyWrapper>
            <PolicyAuditorViewPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/_new/adl',
        element: (
          <LazyWrapper>
            <PolicyAdlEditorPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/:policyId/adl',
        element: (
          <LazyWrapper>
            <PolicyAdlEditorPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'policies/:policyId',
        element: (
          <LazyWrapper>
            <PolicyDetailPage />
          </LazyWrapper>
        ),
      },
      {
        // Alter Integration-Hub: Webhooks/Outbound leben jetzt in Konnektoren;
        // API-Schlüssel werden ein Einstellungen-Tab. Redirect auf Konnektoren.
        path: 'integrations',
        element: <Navigate to="/dashboard/connectors" replace />,
      },
      {
        path: 'team',
        element: <Navigate to="/dashboard/team" replace />,
      },
          {
            path: 'alerts',
            element: (
              <LazyWrapper>
                <AlertsPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'traces',
            element: (
              <LazyWrapper>
                <StubPage title="Decision Log (alt)" />
              </LazyWrapper>
            ),
          },
          {
            path: 'agents',
            element: (
              <LazyWrapper>
                <StubPage title="KI-Systeme (alt)" />
              </LazyWrapper>
            ),
          },
          {
            // Static "new" path MUST sit before the `:agentId` wildcard,
            // otherwise the wildcard captures "new" and SystemDetailPage
            // 404s on a non-existent agent.
            path: 'agents/new',
            element: (
              <LazyWrapper>
                <AgentOnboardingPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'agents/:agentId',
            element: (
              <LazyWrapper>
                <SystemDetailPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'mining',
            element: (
              <LazyWrapper>
                <DecisionMining />
              </LazyWrapper>
            ),
          },
          {
            path: 'anomalies',
            element: (
              <LazyWrapper>
                <StubPage title="Anomalien (alt)" />
              </LazyWrapper>
            ),
          },
          {
            path: 'feedback-analytics',
            element: (
              <LazyWrapper>
                <FeedbackAnalyticsPage />
              </LazyWrapper>
            ),
          },
          {
            path: 'settings',
            element: <Navigate to="/dashboard/settings" replace />,
          },
          {
            path: 'billing',
            element: <Navigate to="/dashboard/billing" replace />,
          },
          // "Activity Log" (was "Audit Log") — renamed 2026-05-15 to disambiguate
          // from "Audit Chain" in the Compliance section.
          {
            path: 'activity-log',
            element: <Navigate to="/dashboard/activity-log" replace />,
          },
          {
            path: 'audit-log',
            element: <Navigate to="/dashboard/o/activity-log" replace />,
          },
          // Settings surfaces (Security / Passkeys / Login-Activity / Privacy)
          // live inside ProfilePage tabs at /dashboard/profile?tab=*. The
          // previously-standalone routes have been removed entirely — internal
          // links (CommandPalette, security-alert emails, in-Profile Manage
          // buttons) all point at the tab URLs directly.
          {
            path: 'reports',
            element: (
              <LazyWrapper>
                <StubPage title="Berichte (alt)" />
              </LazyWrapper>
            ),
          },
          {
            path: 'notifications',
            element: (
              <LazyWrapper>
                <NotificationsPage />
              </LazyWrapper>
            ),
          },

          // Audit Chain
          {
            path: 'audit/chain',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Audit Chain"
                  features={[
                    'SHA-256 + RFC 8785 canonical chain',
                    'Daily Merkle anchors with three independent witness signals (status today): eIDAS RFC 3161 TSA requires EIDAS_TSA_PROD env (D-Trust commercial activation pending) · OpenTimestamps Bitcoin anchor requires OTS_CALENDAR_PRIMARY env · qSeal requires triple-env D-Trust qSeal (pending). Without env: deterministic mock tokens, chain integrity unaffected.',
                    'Continuous heartbeat verification + witness consortium (consortium currently recruiting — 0 external witnesses live as of 2026-05-21)',
                    'On-demand chain verification + JSON/JSONL bundle export',
                  ]}
                >
                  <StubPage title="Audit-Kette (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Evidence Exports
          {
            path: 'audit/exports',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Evidence Exports"
                  features={[
                    'Monthly Notary Attestation PDF (auditor-grade, qSeal-signed in Phase 3.1)',
                    'On-demand Portable Bundle (court-grade tar.gz with verify.sh + methodology)',
                    'Self-contained tamper-evident snapshot for offline regulator verification',
                  ]}
                >
                  <StubPage title="Beweis-Exporte (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Phase 1 — GDPR Art. 17 Erasure (ADR-AT-04)
          {
            path: 'erasure',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="GDPR Erasure"
                  features={[
                    'Two-step protocol: customer pre-flight acknowledgment + payload nullification',
                    'Separate erasure ledger (ADR-AT-04)',
                    'Aligned with EDPB Guidelines 02/2025 (personal data on blockchain) — Adjudon anchors audit-chain hashes via OpenTimestamps',
                  ]}
                >
                  <StubPage title="Löschung (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // TEE Attestation
          {
            path: 'tee-attestation',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="custom"
                  featureName="TEE Attestation"
                  features={[
                    'Provider status (active provider + supported providers + seal-key enabled)',
                    'On-demand payload attestation via TEE quote + integrity hash',
                    'Mock fallback when TEE_PROVIDER env unset (with disclosure)',
                    'Required for Federated rounds + sealed sensitive workflows',
                  ]}
                >
                  <TeeAttestationPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Regulator Replay
          {
            path: 'audit/replay',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="enterprise"
                  featureName="Regulator Replay"
                  features={[
                    'On-demand signed replay artifact per decision trace',
                    'Decision-time signal vector + currently-active calibration map',
                    'HMAC-SHA256 over RFC 8785 canonical JSON — auditor-verifiable offline',
                    'Methodology-drift detection between decision-time map and current map',
                  ]}
                >
                  <RegulatorReplayPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Insurance Feed
          {
            path: 'audit/insurance-feed',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="enterprise"
                  featureName="Insurance Attestation Feed"
                  features={[
                    'Monthly signed JSON feed for AI underwriting partners (Munich Re aiSure target — contract in negotiation; Armilla AI + Lloyd\'s Chaucer in discovery as of 2026-05-21)',
                    'Custom-tier reinsurance integration architecture ready (LD-11) — partner contracts pending',
                    'Partner-pull HMAC-authenticated endpoint live (gated on PARTNER_SECRET_* env per partner)',
                  ]}
                >
                  <InsuranceFeedPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Compliance Mapping
          {
            path: 'compliance/mapping',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="ISO 42001 Mapping"
                  features={[
                    '14 of 38 ISO/IEC 42001:2023 Annex-A controls mapped + 4 Adjudon governance controls',
                    'Coverage status: covered / partial / gap / not-applicable',
                    'Evidence references and audit-ready export',
                    'Continuous gap reporting against the full Annex-A catalogue',
                  ]}
                >
                  <ComplianceMappingPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Provider Tech-Doc Hub
          {
            path: 'provider/technical-documentation',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Provider Tech-Doc Hub"
                  features={[
                    'EU AI Act Art. 11 + Annex IV 9-section technical documentation pack',
                    'EU AI Act Art. 72 Post-Market Monitoring plan with DecisionTrace aggregation',
                    'EU AI Act Art. 53/54/55 GPAI Provider Pack — Enterprise tier (6 documents incl. CycloneDX 1.7 ML-BOM)',
                    'On-demand JSON generation + Bundle Export audit-snapshot link',
                  ]}
                >
                  <Navigate to="/dashboard/eu-ai-act?tab=tech-doc" replace />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // GDPR Art. 30 ROPA
          {
            path: 'compliance/ropa',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="GDPR Art. 30 ROPA"
                  features={[
                    'Auto-generated "Adjudon as Processor" row for your DPO register',
                    'Art. 28 + Art. 30(1)(a-g) coverage incl. sub-processors + safeguards',
                    'JSON + Markdown download for DPO copy-paste',
                    'Auto-syncs to your retention setting + plan tier',
                  ]}
                >
                  <RopaPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Incidents
          {
            path: 'incidents',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Multi-Clock Incident Hub"
                  features={[
                    '5 parallel regulatory clocks (GDPR, AI Act, DORA, NIS2, CRA)',
                    'Deadline tracking with breach prevention',
                    'Linked traces, FRIAs, and auto-generated reports',
                    'Notification dispatch templates',
                  ]}
                >
                  <StubPage title="Vorfälle (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          {
            path: 'incidents/:id',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Multi-Clock Incident Hub"
                  features={[
                    '5 parallel regulatory clocks (GDPR, AI Act, DORA, NIS2, CRA)',
                    'Deadline tracking with breach prevention',
                    'Linked traces, FRIAs, and auto-generated reports',
                    'Notification dispatch templates',
                  ]}
                >
                  <IncidentDetailPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Deployer & FRIA
          {
            path: 'deployer',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Deployer & FRIA"
                  features={[
                    'Art. 26 deployer obligations: oversight, monitoring, DPIA',
                    'Art. 27 Fundamental Rights Impact Assessment wizard',
                    'Provider instruction ingestion + acknowledgement',
                    'Linked FRIAs per deployed system + completeness scoring',
                  ]}
                >
                  <Navigate to="/dashboard/eu-ai-act?tab=deployer-fria" replace />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          {
            path: 'fria',
            element: <Navigate to="/dashboard/eu-ai-act?tab=deployer-fria" replace />,
          },
          // Controller-Responsibility attestation catalog (⚪ non-delegable duties)
          {
            path: 'obligations',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Compliance Obligations"
                  features={[
                    'Your non-delegable controller/deployer duties per framework',
                    'EU AI Act Art. 26(4)/(7)/(11)/(12) deployer obligations',
                    'What you must do + the Adjudon template/tool that supports it',
                    'Per-obligation attestation check-off (the "truly 100%" proof)',
                  ]}
                >
                  <ControllerResponsibilitiesPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Advisory Records
          {
            path: 'advisory',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Advisory Records"
                  features={[
                    'VVG §61/§62 insurance advice documentation',
                    'Automatic §62 protocol generation',
                    'Suitability assessment tracking (IBIP)',
                    'Customer acknowledgement management',
                  ]}
                >
                  <StubPage title="Beratung (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Federated Learning
          {
            path: 'federated',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="enterprise"
                  featureName="Federated Learning"
                  features={[
                    'Pattern families with privacy-budgeted rounds',
                    'Differential privacy ε/δ enforcement',
                    'Sigstore evidence bundles per round',
                    'Cross-tenant policy improvement without data sharing',
                  ]}
                >
                  <FederatedLearningPage />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
          // Privacy Budget
          {
            path: 'privacy-budget',
            element: (
              <LazyWrapper>
                <PrivacyBudgetPage />
              </LazyWrapper>
            ),
          },
          // Provenance
          {
            path: 'provenance',
            element: (
              <LazyWrapper>
                <RequirePlan
                  requiredPlan="governance"
                  featureName="Provenance (C2PA + PROV-O)"
                  features={[
                    'C2PA content credentials with signed claim manifests',
                    'W3C PROV-O JSON-LD export for any decision trace',
                    'Signature verification + manifest revocation',
                    'PII-scrubbed provenance graphs ready for auditors',
                  ]}
                >
                  <StubPage title="Provenance (alt)" />
                </RequirePlan>
              </LazyWrapper>
            ),
          },
        ],
      },

      // ─ NEW IA routes (2026-05-30) — clean /dashboard/* routes ──────────────
      // Interim redirects: clean target paths whose NEW page isn't rebuilt yet
      // → forward to the still-living /dashboard/o/* old page. Posture/Now hrefs
      // already target the clean path; when the real new page lands, swap the
      // <Navigate> for the page element — no downstream href changes needed.
      {
        path: 'deployer',
        element: <Navigate to="/dashboard/eu-ai-act?tab=deployer-fria" replace />,
      },
      {
        path: 'privacy-budget',
        element: <Navigate to="/dashboard/o/privacy-budget" replace />,
      },
      {
        path: 'fria',
        element: <Navigate to="/dashboard/eu-ai-act?tab=deployer-fria" replace />,
      },
      // Systems (renamed from Agents 2026-06-03 — KI-System / AI System).
      // The route param stays :agentId (technical layer keeps "agent";
      // SystemDetailPage reads useParams().agentId).
      {
        path: 'systems',
        element: <LazyWrapper><SystemsPage /></LazyWrapper>,
      },
      {
        path: 'systems/new',
        element: <LazyWrapper><AgentOnboardingPage /></LazyWrapper>,
      },
      {
        path: 'systems/:agentId',
        element: <LazyWrapper><SystemDetailPage /></LazyWrapper>,
      },
      // Back-compat redirects for the retired /dashboard/agents* slug.
      {
        path: 'agents',
        element: <RedirectAgentsToSystems />,
      },
      {
        path: 'agents/new',
        element: <Navigate to="/dashboard/systems/new" replace />,
      },
      {
        path: 'agents/:agentId',
        element: <RedirectAgentDetailToSystem />,
      },
      {
        path: 'connectors',
        element: <LazyWrapper><ConnectorsPage /></LazyWrapper>,
      },
      {
        // Old Operations slug → Connectors (IA rename 2026-06-03: outbound
        // channel ecosystem). Redirect keeps the half-migrated cross-links
        // (Overview connect-CTAs, legacy pages) working until each is repointed.
        path: 'integrations',
        element: <Navigate to="/dashboard/connectors" replace />,
      },
      {
        path: 'decision-log',
        element: <LazyWrapper><DecisionLogPage /></LazyWrapper>,
      },
      {
        path: 'review-queue',
        element: <LazyWrapper><ReviewQueuePage /></LazyWrapper>,
      },
      {
        path: 'anomalies',
        element: <LazyWrapper><AnomaliesPage /></LazyWrapper>,
      },
      {
        path: 'audit-chain',
        element: <LazyWrapper><AuditChainPage /></LazyWrapper>,
      },
      {
        path: 'provenance',
        element: <LazyWrapper><ProvenancePage /></LazyWrapper>,
      },
      {
        path: 'evidence-exports',
        element: <LazyWrapper><EvidenceExportsPage /></LazyWrapper>,
      },
      {
        path: 'incidents',
        element: (
          <LazyWrapper>
            <RequirePlan
              requiredPlan="governance"
              featureName="Multi-Clock Incident Hub"
              features={[
                '5 parallele Melde-Uhren (DSGVO, EU AI Act, DORA, NIS2, CRA)',
                'Fristen-Verfolgung mit Eskalations-Checkpoints',
                'Verknüpfte KI-Systeme + Melde-Entwürfe',
              ]}
            >
              <IncidentsPage />
            </RequirePlan>
          </LazyWrapper>
        ),
      },
      {
        // Incident detail (deep-link target from the Status-tab Fristen band).
        path: 'incidents/:id',
        element: <LazyWrapper><IncidentDetailPage /></LazyWrapper>,
      },
      {
        path: 'erasure',
        element: <LazyWrapper><ErasurePage /></LazyWrapper>,
      },
      {
        path: 'policy-engine',
        element: <LazyWrapper><PolicyEnginePage /></LazyWrapper>,
      },
      {
        path: 'calibration-confidence',
        element: <LazyWrapper><CalibrationConfidencePage /></LazyWrapper>,
      },
      {
        path: 'fairness-bias',
        element: <LazyWrapper><FairnessBiasPage /></LazyWrapper>,
      },
      {
        path: 'compliance-overview',
        element: <LazyWrapper><ComplianceOverviewPage /></LazyWrapper>,
      },
      {
        path: 'eu-ai-act',
        element: <LazyWrapper><EuAiActPage /></LazyWrapper>,
      },
      {
        path: 'gdpr',
        element: <LazyWrapper><GdprPage /></LazyWrapper>,
      },
      {
        path: 'aml-gwg',
        element: <LazyWrapper><AmlGwgPage /></LazyWrapper>,
      },
      {
        path: 'beratung',
        element: <LazyWrapper><BeratungPage /></LazyWrapper>,
      },
      {
        path: 'dora',
        element: <LazyWrapper><DoraPage /></LazyWrapper>,
      },
      {
        // DORA-Register ist in den DORA-Hub gefaltet (Tab "Informationsregister").
        // Redirect erhaelt die 4 Katalog-Deep-Links (/dashboard/dora-register).
        path: 'dora-register',
        element: <Navigate to="/dashboard/dora?tab=register" replace />,
      },
      {
        path: 'solvency-vag',
        element: <LazyWrapper><SolvencyVagPage /></LazyWrapper>,
      },
      {
        // EIOPA AI-Opinion hub — 9th Versicherer-SSOT framework (the one gap).
        // Stub for now; tabs (board governance / fairness / robustness / ORSA-AI)
        // come with the Compliance build.
        path: 'eiopa',
        element: <LazyWrapper><EiopaPage /></LazyWrapper>,
      },
      {
        path: 'data-act-fida',
        element: <LazyWrapper><DataActFidaPage /></LazyWrapper>,
      },
      {
        path: 'analytics',
        element: <LazyWrapper><AnalyticsPage /></LazyWrapper>,
      },
      {
        path: 'reports',
        element: <LazyWrapper><ReportsPage /></LazyWrapper>,
      },
      // ── ADMIN (2026-06-13: aus /dashboard/o zurück auf Default-Routen migriert).
      // Design-Rebuild folgt seitenweise; API-Schlüssel wird ein Einstellungen-Tab.
      {
        path: 'team',
        element: <LazyWrapper><TeamManagement /></LazyWrapper>,
      },
      {
        path: 'billing',
        element: <LazyWrapper><BillingPage /></LazyWrapper>,
      },
      {
        path: 'settings',
        element: <LazyWrapper><SettingsPage /></LazyWrapper>,
      },
      {
        path: 'activity-log',
        element: <LazyWrapper><AuditLogPage /></LazyWrapper>,
      },
    ],
  },

  // 404 Catch-all
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);